import { Fragment, type ReactNode, useRef } from "react";
import { useFormContext } from "react-hook-form";

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../primitives/form";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "../primitives/select";

/** A single selectable option. */
export type SelectOption = {
  /** Value submitted with the form. Must match the string expected by the Zod schema. */
  value: string;
  /** Text or element shown in the dropdown and in the trigger after selection. */
  label: ReactNode;
  disabled?: boolean;
};

/** A labeled group of related options rendered under a section header. */
export type SelectOptionGroup = {
  /** Section header shown above the group's items. */
  label: string;
  items: SelectOption[];
};

type ColocaSelectFieldProps = {
  /** Field name as registered in the parent `useForm` schema. Must match the Zod key exactly. */
  name: string;
  /** Visible label rendered above the trigger. */
  label?: ReactNode;
  /** Helper text rendered below the trigger. Use for format hints or constraints. Do not repeat the label. */
  description?: ReactNode;
  /** Placeholder shown in the trigger when no value is selected. */
  placeholder?: string;
  /** Flat list of options. Use when options need no grouping. Mutually exclusive with `groups`. */
  items?: SelectOption[];
  /**
   * Grouped options rendered under labeled section headers. Use when options benefit from categorization —
   * e.g. countries by region, or currencies by continent. Mutually exclusive with `items`.
   */
  groups?: SelectOptionGroup[];
  /** Visual size of the trigger. Defaults to `"default"`. */
  size?: "sm" | "default";
  disabled?: boolean;
  /**
   * Marks the field as required: an asterisk on the label and `aria-required` on the trigger. Presentational
   * — the Zod schema still owns validation.
   */
  required?: boolean;
};

/**
 * Controlled select wired to the nearest React Hook Form context.
 *
 * Use inside a `<ColocaForm>` for any single-choice dropdown field. Pass a flat list via `items` or a
 * categorized list via `groups`. Validation errors from the Zod schema are displayed automatically below the
 * trigger via `<FormMessage>`.
 *
 * For multi-select or combobox use cases use the corresponding primitives directly.
 */
export function ColocaSelectField({
  name,
  label,
  description,
  placeholder,
  items,
  groups,
  size,
  disabled,
  required,
}: ColocaSelectFieldProps) {
  const { control } = useFormContext();
  const allOptions = [...(items ?? []), ...(groups ?? []).flatMap((group) => group.items)];
  // Lets the popup open at the trigger's width instead of widening a frame later. See `SelectContent`.
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <FormItem required={required}>
          {label && <FormLabel>{label}</FormLabel>}
          <Select
            value={field.value ?? null}
            items={allOptions}
            onValueChange={(value) => field.onChange(value)}
            onOpenChange={(open) => {
              if (!open) field.onBlur();
            }}
            disabled={disabled}
          >
            <FormControl>
              <SelectTrigger
                ref={triggerRef}
                size={size}
                className="w-full"
                aria-invalid={fieldState.invalid}
              >
                <SelectValue placeholder={placeholder} />
              </SelectTrigger>
            </FormControl>
            <SelectContent anchorRef={triggerRef}>
              <SelectGroup>
                {items?.map((item) => (
                  <SelectItem key={item.value} value={item.value} disabled={item.disabled}>
                    {item.label}
                  </SelectItem>
                ))}
              </SelectGroup>
              {groups?.map((group, i) => (
                <Fragment key={group.label}>
                  {i > 0 && <SelectSeparator />}
                  <SelectGroup>
                    <SelectLabel>{group.label}</SelectLabel>
                    {group.items.map((item) => (
                      <SelectItem key={item.value} value={item.value} disabled={item.disabled}>
                        {item.label}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </Fragment>
              ))}
            </SelectContent>
          </Select>
          {description && <FormDescription>{description}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
